import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Plus, Coffee, Edit, Trash2, Calendar, MapPin } from 'lucide-react';
import toast from 'react-hot-toast';
import api from '../../config/api';
import { useShop } from '../../context/ShopContext';
import AdvancedPostModal from '../../components/AdvancedPostModal';
import logger from '../../utils/logger';

const PostsTab = () => {
  const { t } = useTranslation();
  const { shop } = useShop();
  const [posts, setPosts] = useState([]);
  const [postsLoading, setPostsLoading] = useState(false);
  const [showAddPost, setShowAddPost] = useState(false);
  const [editingPost, setEditingPost] = useState(null);

  const loadPosts = useCallback(async () => {
    if (!shop?.slug) return;
    setPostsLoading(true);
    try {
      const response = await api.get(`/shop/${shop.slug}/posts`);
      const data = response.data;
      if (data.success) {
        setPosts(data.data || []);
      } else {
        toast.error(data.message || t('dashboard.failedToLoadPosts','Failed to load posts'));
      }
    } catch (e) {
      logger.error('Error loading posts:', e);
      toast.error(t('dashboard.failedToLoadPosts','Failed to load posts'));
    } finally {
      setPostsLoading(false);
    }
  }, [shop?.slug, t]);

  useEffect(() => { loadPosts(); }, [loadPosts]);

  const createAdvancedPost = async (postData) => {
    try {
      const response = await api.post(`/posts/shop/${shop.id}`, {
        content: postData.content,
        location: postData.location,
        images: postData.images || []
      });
      const data = response.data;
      if (data.success) {
        toast.success(data.message || t('posts.created','Post created successfully'));
        setShowAddPost(false);
        loadPosts();
      } else {
        toast.error(data.message || t('posts.createFailed','Failed to create post'));
      }
    } catch (e) {
      logger.error('Error creating post:', e);
      toast.error(t('posts.createFailed','Failed to create post'));
    }
  };

  const editPost = async (postId, postData) => {
    try {
      const response = await api.put(`/posts/${postId}`, {
        content: postData.content,
        location: postData.location,
        images: postData.images || []
      });
      if (response.data.success) {
        toast.success(response.data.message || t('posts.updated','Post updated successfully'));
        setEditingPost(null);
        loadPosts();
      } else {
        toast.error(response.data.message || t('posts.updateFailed','Failed to update post'));
      }
    } catch (e) {
      logger.error('Error updating post:', e);
      toast.error(t('posts.updateFailed','Failed to update post'));
    }
  };

  const deletePost = async (postId) => {
    if (!window.confirm(t('posts.confirmDelete','Are you sure you want to delete this post?'))) return;
    try {
      const response = await api.delete(`/posts/${postId}`);
      if (response.data.success !== false) {
        toast.success(t('posts.deleted','Post deleted successfully'));
        loadPosts();
      } else {
        toast.error(response.data.message || t('posts.deleteFailed','Failed to delete post'));
      }
    } catch (e) {
      logger.error('Error deleting post:', e);
      toast.error(t('posts.deleteFailed','Failed to delete post'));
    }
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-bold text-white">{t('dashboard.posts')}</h2>
        <button onClick={() => setShowAddPost(true)} className="btn-primary flex items-center gap-2">
          <Plus className="w-4 h-4" /> {t('dashboard.addPost')}
        </button>
      </div>
      <AdvancedPostModal
        isOpen={showAddPost || editingPost !== null}
        onClose={() => { setShowAddPost(false); setEditingPost(null); }}
        onSubmit={editingPost ? (data) => editPost(editingPost.id, data) : createAdvancedPost}
        loading={postsLoading}
        editingPost={editingPost}
      />
      {postsLoading ? (
        <div className="text-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#a15507] mx-auto mb-4" />
          <p className="text-white text-lg">{t('dashboard.loadingPosts')}</p>
        </div>
      ) : posts.length === 0 ? (
        <div className="text-center py-12">
          <Coffee className="w-16 h-16 text-gray-400 mx-auto mb-4" />
          <h3 className="text-xl font-semibold text-white mb-2">{t('dashboard.noPosts')}</h3>
          <p className="text-gray-400 mb-4">{t('dashboard.noPostsDescription')}</p>
          <button onClick={() => setShowAddPost(true)} className="btn-primary">{t('dashboard.createFirstPost')}</button>
        </div>
      ) : (
        <div className="space-y-6">
          {posts.map(post => (
            <div key={post.id} className="card">
              <div className="flex justify-between items-start mb-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-gradient-to-r from-[#a15507] to-[#924e06] flex items-center justify-center">
                    <Coffee className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <h3 className="text-white font-semibold">{shop?.name}</h3>
                    <p className="text-gray-400 text-sm">{new Date(post.createdAt).toLocaleDateString()}</p>
                  </div>
                </div>
                <div className="flex items-center space-x-2">
                  <button onClick={() => setEditingPost(post)} className="text-blue-400 hover:text-blue-300" title={t('common.edit')}><Edit className="w-5 h-5" /></button>
                  <button onClick={() => deletePost(post.id)} className="text-red-400 hover:text-red-300" title={t('common.delete')}><Trash2 className="w-5 h-5" /></button>
                </div>
              </div>
              {post.content && (
                <div className="text-gray-300 mb-4">
                  <p className="whitespace-pre-wrap">{post.content}</p>
                  {post.hashtags?.length > 0 && (
                    <div className="flex flex-wrap gap-2 mt-3">
                      {post.hashtags.map((h,i)=>(<span key={i} className="px-2 py-1 bg-amber-600/20 text-amber-400 rounded-full text-sm">#{h}</span>))}
                    </div>
                  )}
                  {post.location && (
                    <div className="flex items-center gap-2 mt-3 text-gray-400"><MapPin className="w-4 h-4" /><span className="text-sm">{post.location}</span></div>
                  )}
                </div>
              )}
              {post.images?.length > 0 && (
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mb-4">
                  {post.images.map((img,i)=>(<img key={i} src={img} alt={`Post ${i+1}`} className="w-full h-32 object-cover rounded-lg" />))}
                </div>
              )}
              <div className="flex items-center gap-4 text-gray-400 text-sm flex-wrap">
                <div className="flex items-center gap-2"><Calendar className="w-4 h-4" /><span>{new Date(post.createdAt).toLocaleDateString()}</span></div>
                {post.location && <div className="flex items-center gap-2"><MapPin className="w-4 h-4" /><span>{post.location}</span></div>}
                {post.category && <div className="px-2 py-1 bg-[#a15507] text-white text-xs rounded-full">{post.category}</div>}
                {post.price && <div className="font-semibold text-green-400">{post.price}</div>}
                {post.isFeatured && <div className="px-2 py-1 bg-yellow-500 text-black text-xs rounded-full font-bold">{t('posts.featuredBadge')}</div>}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default PostsTab;
